import { useState, useEffect } from 'react';
import { getFaqApiUrl } from '../../config/globalConfig';

const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

export default function Faqs() {
    const [activeCategory, setActiveCategory] = useState('general');
    const [activeFaq, setActiveFaq] = useState(null);
    const [faqs, setFaqs] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchFaqs();
    }, []);

    const fetchFaqs = async () => {
        try {
            setLoading(true);
            const API_URL = getFaqApiUrl('GET_ALL');
            const response = await fetch(API_URL);
            const data = await response.json();
            
            if (data.result && data.Faqs) {
                // Group FAQs by type
                const groupedFaqs = data.Faqs.reduce((acc, faq) => {
                    const type = faq.type || 'general';
                    if (!acc[type]) {
                        acc[type] = [];
                    }
                    acc[type].push({
                        id: faq.id,
                        question: faq.title,
                        answer: faq.content
                    });
                    return acc;
                }, {});
                setFaqs(groupedFaqs);
            } else {
                setError('No FAQs found');
            }
        } catch (err) {
            console.error('Error fetching FAQs:', err);
            setError('Failed to load FAQs');
            // Fallback to static FAQs if API fails
            setFaqs({
                general: [
                    { id: 1, question: "What is the minimum age to join the academy?", answer: "We accept students from 5 years and above. Certain advanced courses may have age or skill prerequisites." },
                    { id: 2, question: "Are the classes online or offline?", answer: "We offer both online and offline classes depending on the course and location." },
                    { id: 3, question: "How long is each session?", answer: "Sessions are 50–60 minutes." }
                ],
                students: [
                    { id: 4, question: "Do I need to own an instrument to join?", answer: "For instrumental courses, having your own instrument is mandatory to learn the instrument and for self practice." }
                ],
                parents: [],
                career: [],
                performance: []
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryClick = (category) => {
        setActiveCategory(category);
        setActiveFaq(null); // Reset FAQ items to closed state
    };

    const handleFaqClick = (faqId) => {
        // If the clicked item is already active, close it
        if (activeFaq === faqId) {
            setActiveFaq(null);
        } else {
            // Otherwise, open the clicked item and close others
            setActiveFaq(faqId);
        }
    };

    // Helper function to render FAQs for a specific category
    const renderFaqsForCategory = (category) => {
        const categoryFaqs = faqs[category] || [];
        
        if (categoryFaqs.length === 0) {
            return (
                <div className="no-faqs">
                    <p>No FAQs available for this category.</p>
                </div>
            );
        }

        return categoryFaqs.map((faq) => (
            <div
                key={faq.id}
                className={`faq-item ${activeFaq === faq.id.toString() ? 'active' : ''}`}
                onClick={() => handleFaqClick(faq.id.toString())}
            >
                <div className="faq-question">
                    <h3 className="question-text">{faq.question}</h3>
                    <div className="faq-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    </div>
                </div>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: decodeHtml(faq.answer) }} />
            </div>
        ));
    };

    if (loading) {
        return (
            <section id="faq-section">
                <div className="faq-container">
                    <div className="faq-header">
                        <h2 className="section-title">Frequently Asked Questions</h2>
                        <p className="section-subtitle">Find answers to common questions about our music school</p>
                    </div>
                    <div className="loading-state">
                        <p>Loading FAQs...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section id="faq-section">
                <div className="faq-container">
                    <div className="faq-header">
                        <h2 className="section-title">Frequently Asked Questions</h2>
                        <p className="section-subtitle">Find answers to common questions about our music school</p>
                    </div>
                    <div className="error-state">
                        <p>{error}</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <section id="faq-section">
                <div className="faq-container">
                    <div className="faq-header">
                        <h2 className="section-title">Frequently Asked Questions</h2>
                        <p className="section-subtitle">Find answers to common questions about our music school</p>
                    </div>

                    <div className="faq-categories">
                        <button
                            className={`faq-category-btn ${activeCategory === 'general' ? 'active' : ''}`}
                            onClick={() => handleCategoryClick('general')}
                        >
                            General FAQs
                        </button>
                        <button
                            className={`faq-category-btn ${activeCategory === 'students' ? 'active' : ''}`}
                            onClick={() => handleCategoryClick('students')}
                        >
                            For Students
                        </button>
                        <button
                            className={`faq-category-btn ${activeCategory === 'parents' ? 'active' : ''}`}
                            onClick={() => handleCategoryClick('parents')}
                        >
                            For Parents
                        </button>
                        <button
                            className={`faq-category-btn ${activeCategory === 'career' ? 'active' : ''}`}
                            onClick={() => handleCategoryClick('career')}
                        >
                            Career Support
                        </button>
                        <button
                            className={`faq-category-btn ${activeCategory === 'performance' ? 'active' : ''}`}
                            onClick={() => handleCategoryClick('performance')}
                        >
                            Performance Opportunities
                        </button>
                    </div>

                    <div className="faq-content">
                        <div className={`faq-category-content ${activeCategory === 'general' ? 'active' : ''}`}>
                            {renderFaqsForCategory('general')}
                        </div>

                        <div className={`faq-category-content ${activeCategory === 'students' ? 'active' : ''}`}>
                            {renderFaqsForCategory('students')}
                        </div>

                        <div className={`faq-category-content ${activeCategory === 'parents' ? 'active' : ''}`}>
                            {renderFaqsForCategory('parents')}
                        </div>

                        <div className={`faq-category-content ${activeCategory === 'career' ? 'active' : ''}`}>
                            {renderFaqsForCategory('career')}
                        </div>

                        <div className={`faq-category-content ${activeCategory === 'performance' ? 'active' : ''}`}>
                            {renderFaqsForCategory('performance')}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
} 